import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { toast } from 'react-toastify';
import {
  C,
  imgUrl,
  normalizeImgField,
  nx,
  ny,
  resolveFeatureIconAndLabel,
  SLOT_RESULTS_ADD_H,
  SLOT_RESULTS_HEADER_H,
  SLOT_RESULTS_ROW_GAP,
  SLOT_RESULTS_ROW_H,
  SLOT_RESULTS_W,
} from '../flowCanvas.constants';

const BORDER_R = 8;
const HEADER_H = 32;
const DELETE_FILL = '#dc2626';
const PICKER_MENU_W = 210;
const PICKER_MENU_MAX_H = 180;
const PICKER_MENU_PAD = 6;
const PICKER_VIEWPORT_PAD = 12;
const RED_ODDS_DENOMINATOR = 250;
const ANY_RESULT_VALUE = '';
const ANY_RESULT_LABEL = 'Any';
const ANY_RESULT_SYMBOL = '*';
const COMMON_ODDS_FRACTIONS = [
  { numerator: 1, denominator: 20 },
  { numerator: 1, denominator: 12 },
  { numerator: 1, denominator: 10 },
  { numerator: 1, denominator: 8 },
  { numerator: 1, denominator: 6 },
  { numerator: 1, denominator: 5 },
  { numerator: 1, denominator: 4 },
  { numerator: 1, denominator: 3 },
  { numerator: 2, denominator: 5 },
  { numerator: 1, denominator: 2 },
  { numerator: 3, denominator: 5 },
  { numerator: 2, denominator: 3 },
  { numerator: 3, denominator: 4 },
  { numerator: 4, denominator: 5 },
  { numerator: 5, denominator: 6 },
  { numerator: 7, denominator: 8 },
  { numerator: 9, denominator: 10 },
  { numerator: 11, denominator: 12 },
];

const getGreatestCommonDivisor = (a, b) => {
  let left = Math.abs(Math.round(a));
  let right = Math.abs(Math.round(b));
  while (right !== 0) {
    const next = left % right;
    left = right;
    right = next;
  }
  return left || 1;
};

const getPickerPosition = (triggerRect) => {
  const menuH = PICKER_MENU_MAX_H + PICKER_MENU_PAD * 2 + 2;
  const preferredTop = triggerRect.top - menuH - 6;
  const fallbackTop = triggerRect.bottom + 6;
  const top = preferredTop >= PICKER_VIEWPORT_PAD
    ? preferredTop
    : Math.min(fallbackTop, window.innerHeight - menuH - PICKER_VIEWPORT_PAD);
  const left = Math.min(
    Math.max(PICKER_VIEWPORT_PAD, triggerRect.left),
    window.innerWidth - PICKER_MENU_W - PICKER_VIEWPORT_PAD,
  );

  return { top, left };
};

const getItemCount = (item) => Math.max(0, Number(item?.amountAvailable) || 0);

const getSlotSettings = (liveCustomisationData) => {
  const values = liveCustomisationData?.setup?.globalSettings?.slotSettings;
  return Array.isArray(values) ? values[0] || {} : {};
};

const getResultRows = (step, liveCustomisationData) => {
  const cfg = step?.customisationConfig;
  const varName = cfg?.variables?.[0]?.variableName;
  const canonicalLiveVals = liveCustomisationData?.setup?.resultTriggers?.setup;
  const liveVals = Array.isArray(canonicalLiveVals)
    ? canonicalLiveVals
    : (cfg?.tabKey && cfg?.varKey && varName
      ? liveCustomisationData?.[cfg.tabKey]?.[cfg.varKey]?.[varName]
      : null);
  const source = Array.isArray(liveVals)
    ? liveVals
    : (step?.populatedData?.variables?.[0]?.allValues || []);
  return source.filter(item => item != null);
};

const getResultOptionLabel = (item, reelIndex, itemIndex) => {
  const explicitName = String(item?.name || '').trim();
  if (explicitName) return explicitName;

  const imageField = normalizeImgField(item?.image);
  const imageName = String(imageField.name || '').trim();
  if (imageName) return imageName.replace(/\.[^.]+$/, '');

  return `Item ${reelIndex + 1}.${itemIndex + 1}`;
};

const getReelOptions = (liveCustomisationData, reelIndex) => {
  const groupName = `options${reelIndex + 1}`;
  const values = liveCustomisationData?.setup?.[groupName]?.options;
  return Array.isArray(values)
    ? values
      .map((item, itemIndex) => {
        if (item == null) return null;
        const explicitName = String(item?.name || '').trim();
        return {
          ...item,
          __resultOptionKey: `option-${reelIndex}-${itemIndex}`,
          __resultOptionLabel: getResultOptionLabel(item, reelIndex, itemIndex),
          __resultOptionValue: explicitName,
          __resultOptionSelectable: !!explicitName,
        };
      })
      .filter(item => item != null)
    : [];
};

const getAnyResultOption = (reelIndex) => ({
  __resultOptionKey: `any-${reelIndex}`,
  __resultOptionLabel: ANY_RESULT_LABEL,
  __resultOptionValue: ANY_RESULT_VALUE,
  __resultOptionSelectable: true,
  __resultOptionIsAny: true,
});

const isOptInReel = (slotSettings, reelIndex) => !!slotSettings?.[`useOptInList_reel${reelIndex + 1}`];

const getResultFieldKey = (reelIndex) => `option${reelIndex + 1}`;

const getComboChanceData = (row, reelCount, slotSettings, liveCustomisationData) => {
  let numerator = 1;
  let denominator = 1;
  let hasSelectableReel = false;

  for (let reelIndex = 0; reelIndex < reelCount; reelIndex += 1) {
    if (isOptInReel(slotSettings, reelIndex)) continue;

    hasSelectableReel = true;
    const selectedValue = row?.[getResultFieldKey(reelIndex)];
    if (selectedValue == null) return null;

    const options = getReelOptions(liveCustomisationData, reelIndex);
    const total = options.reduce((sum, option) => sum + getItemCount(option), 0);
    if (selectedValue === ANY_RESULT_VALUE) {
      if (!total) return null;
      continue;
    }

    const match = options.find((option) => option?.__resultOptionValue === selectedValue || option?.name === selectedValue);
    const amount = getItemCount(match);
    if (!match || !total || !amount) return null;
    numerator *= amount;
    denominator *= total;
    const divisor = getGreatestCommonDivisor(numerator, denominator);
    numerator /= divisor;
    denominator /= divisor;
  }

  if (!hasSelectableReel) return null;
  return {
    percentage: (numerator / denominator) * 100,
    numerator,
    denominator,
  };
};

const formatChance = (chance) => {
  if (chance == null) return 'Incomplete';
  if (chance >= 10) return `${chance.toFixed(1)}%`;
  return `${chance.toFixed(2)}%`;
};

const getChanceBadgeStyles = (chanceData) => {
  if (!chanceData) {
    return {
      color: C.textDim,
      background: 'var(--fc-circle-bg)',
      borderColor: C.border,
    };
  }

  const chancePct = Math.max(0, Math.min(100, Number(chanceData.percentage) || 0));
  const redThresholdPct = 100 / RED_ODDS_DENOMINATOR;
  const normalized = Math.max(0, Math.min(1, (chancePct - redThresholdPct) / (100 - redThresholdPct)));
  const hue = Math.round(normalized * 120);

  return {
    color: `hsl(${hue}, 72%, var(--fc-chance-text-l))`,
    background: `hsla(${hue}, 72%, 44%, 0.14)`,
    borderColor: `hsl(${hue}, 72%, var(--fc-chance-stroke-l))`,
  };
};

const getReadableChanceFraction = (chanceData) => {
  if (!chanceData) return null;

  const numerator = Math.max(0, Number(chanceData.numerator) || 0);
  const denominator = Math.max(1, Number(chanceData.denominator) || 1);
  if (!numerator) return null;

  if (numerator >= denominator) return 'Certain';
  if (numerator === 1) return `1 in ${Math.max(2, Math.round(denominator / numerator))}`;

  const chance = numerator / denominator;
  const bestFit = COMMON_ODDS_FRACTIONS.reduce((best, candidate) => {
    const candidateChance = candidate.numerator / candidate.denominator;
    const diff = Math.abs(candidateChance - chance);
    if (!best || diff < best.diff) {
      return { ...candidate, diff };
    }
    return best;
  }, null);

  if (!bestFit) return null;
  if (bestFit.numerator === 1) return `1 in ${bestFit.denominator}`;
  return `${bestFit.numerator}/${bestFit.denominator}`;
};

const formatChanceFraction = (chanceData) => {
  return getReadableChanceFraction(chanceData);
};

const trashButtonStyle = {
  border: 'none',
  background: DELETE_FILL,
  borderRadius: '999px',
  width: '18px',
  height: '18px',
  cursor: 'pointer',
  padding: 0,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
};

const trashIconStyle = {
  position: 'relative',
  width: '12px',
  height: '12px',
  pointerEvents: 'none',
  userSelect: 'none',
};

const trashLidStyle = {
  position: 'absolute',
  left: '3.5px',
  top: '0px',
  width: '5px',
  height: '2.5px',
  borderRadius: '1.2px',
  background: '#fee2e2',
};

const trashBarStyle = {
  position: 'absolute',
  left: '1px',
  top: '2px',
  width: '10px',
  height: '2px',
  borderRadius: '1px',
  background: '#fee2e2',
};

const trashBodyStyle = {
  position: 'absolute',
  left: '2.5px',
  top: '4.5px',
  width: '7px',
  height: '6px',
  borderRadius: '1px',
  background: '#fee2e2',
};

const trashLineBaseStyle = {
  position: 'absolute',
  top: '6px',
  width: '1.2px',
  height: '4px',
  borderRadius: '0.6px',
  background: '#dc2626',
};

const isDuplicateCombo = (rows, nextRow, rowIndex, reelCount, slotSettings) => {
  const activeKeys = Array.from({ length: reelCount }, (_, reelIndex) => reelIndex)
    .filter((reelIndex) => !isOptInReel(slotSettings, reelIndex))
    .map((reelIndex) => getResultFieldKey(reelIndex));

  if (!activeKeys.length) return false;
  if (activeKeys.every((key) => String(nextRow?.[key] ?? '') === ANY_RESULT_VALUE)) return false;

  return rows.some((row, index) => {
    if (index === rowIndex) return false;
    return activeKeys.every((key) => String(row?.[key] || '').trim() === String(nextRow?.[key] || '').trim());
  });
};

const menuItemStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  padding: '7px 8px',
  background: 'transparent',
  border: 'none',
  color: C.text,
  width: '100%',
  cursor: 'pointer',
  textAlign: 'left',
  borderRadius: '8px',
};

const getResultNodeHeight = (step, liveCustomisationData) => {
  const rows = getResultRows(step, liveCustomisationData);
  const rowCount = Math.max(rows.length, 1);
  return SLOT_RESULTS_HEADER_H + rowCount * (SLOT_RESULTS_ROW_H + SLOT_RESULTS_ROW_GAP) - SLOT_RESULTS_ROW_GAP + SLOT_RESULTS_ADD_H + 18;
};

const SlotResultsNode = ({
  step,
  selected,
  onClick,
  centreY,
  overrideX,
  overrideY,
  campaignID,
  campaignFeatures,
  liveCustomisationData,
  activeGraphicIndex,
  onGraphicClick,
  onFlowFieldChange,
  onSlotResultAdd,
  onSlotResultRemove,
  onSlotResultDisruptorDrop,
  onSlotResultDisruptorRemove,
}) => {
  const [openPicker, setOpenPicker] = useState(null);
  const pickerMenuRef = useRef(null);
  const x = overrideX ?? nx(step);
  const y = overrideY ?? ny(step, centreY);
  const rows = getResultRows(step, liveCustomisationData);
  const slotSettings = getSlotSettings(liveCustomisationData);
  const reelCount = Math.max(1, Math.min(5, Number(slotSettings?.reels) || 3));
  const nodeH = getResultNodeHeight(step, liveCustomisationData);
  const topY = y - nodeH / 2;
  const leftX = x - SLOT_RESULTS_W / 2;
  const cfg = step?.customisationConfig;
  const varName = cfg?.variables?.[0]?.variableName;
  const renderedRows = rows.length > 0 ? rows : [{ option1: '', option2: '', option3: '', option4: '', option5: '', triggerDisruptor: '' }];

  useEffect(() => {
    if (!openPicker) return undefined;

    const handlePointerDown = (event) => {
      if (pickerMenuRef.current?.contains(event.target) || openPicker.triggerElement?.contains(event.target)) {
        return;
      }
      setOpenPicker(null);
    };
    const handleEscape = (event) => {
      if (event.key === 'Escape') setOpenPicker(null);
    };
    const handleViewportChange = (event) => {
      const eventTarget = event?.target;
      if (eventTarget && pickerMenuRef.current?.contains(eventTarget)) {
        return;
      }

      setOpenPicker((current) => {
        if (!current?.triggerElement) return null;

        const triggerRect = current.triggerElement.getBoundingClientRect();
        const pickerPosition = getPickerPosition(triggerRect);
        return {
          ...current,
          ...pickerPosition,
        };
      });
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleEscape);
    window.addEventListener('resize', handleViewportChange);
    window.addEventListener('scroll', handleViewportChange, true);

    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleEscape);
      window.removeEventListener('resize', handleViewportChange);
      window.removeEventListener('scroll', handleViewportChange, true);
    };
  }, [openPicker]);

  const handleResultOptionSelect = (rowIndex, fieldKey, value) => {
    const currentRow = renderedRows[rowIndex] || {};
    const nextRow = { ...currentRow, [fieldKey]: value };
    if (isDuplicateCombo(renderedRows, nextRow, rowIndex, reelCount, slotSettings)) {
      toast.error('That winning combo already exists.');
      return;
    }
    onFlowFieldChange?.('setup', 'resultTriggers', 'setup', rowIndex, fieldKey, value);
    setOpenPicker(null);
  };
  const openPickerRow = openPicker ? renderedRows[openPicker.rowIndex] || null : null;
  const openPickerFieldKey = openPicker ? getResultFieldKey(openPicker.reelIndex) : null;
  const openPickerOptions = openPicker
    ? [getAnyResultOption(openPicker.reelIndex), ...getReelOptions(liveCustomisationData, openPicker.reelIndex)]
    : [];

  return (
    <>
    <g onClick={onClick} style={{ cursor: 'pointer' }}>
      <rect
        x={leftX}
        y={topY}
        width={SLOT_RESULTS_W}
        height={nodeH}
        rx={BORDER_R}
        fill={selected ? C.cardSel : C.card}
        stroke={selected ? C.sel : C.border}
        strokeWidth={selected ? 2 : 1.5}
      />

      <clipPath id={`slot-results-hdr-${step.stepId}`}>
        <rect x={leftX} y={topY} width={SLOT_RESULTS_W} height={HEADER_H} rx={BORDER_R} ry={BORDER_R} />
      </clipPath>
      <rect
        x={leftX}
        y={topY}
        width={SLOT_RESULTS_W}
        height={HEADER_H + BORDER_R}
        clipPath={`url(#slot-results-hdr-${step.stepId})`}
        fill="rgba(145,70,255,0.22)"
      />

      <text x={leftX + 14} y={topY + 21} fill={C.text} fontSize="11.5" fontWeight="700">
        Winning Results Triggers
      </text>
      <text x={leftX + 14} y={topY + 45} fill={C.textMuted} fontSize="8.8">
        Build winning reel combos and assign a trigger component to each.
      </text>

      <foreignObject x={leftX + 12} y={topY + SLOT_RESULTS_HEADER_H} width={SLOT_RESULTS_W - 24} height={nodeH - SLOT_RESULTS_HEADER_H - 8}>
        <div xmlns="http://www.w3.org/1999/xhtml" style={{ display: 'flex', flexDirection: 'column', gap: `${SLOT_RESULTS_ROW_GAP}px`, fontFamily: 'inherit' }}>
          {renderedRows.map((row, rowIndex) => {
            const triggerMeta = resolveFeatureIconAndLabel(row?.triggerDisruptor, campaignFeatures);
            const comboChanceData = getComboChanceData(row, reelCount, slotSettings, liveCustomisationData);
            const comboChance = comboChanceData?.percentage ?? null;
            const comboChanceFraction = formatChanceFraction(comboChanceData);
            const chanceBadgeStyles = getChanceBadgeStyles(comboChanceData);
            const isRowActive = activeGraphicIndex === rowIndex;
            const previews = Array.from({ length: reelCount }).map((_, reelIndex) => {
              const fieldKey = getResultFieldKey(reelIndex);
              const selectedValue = row?.[fieldKey];
              const options = getReelOptions(liveCustomisationData, reelIndex);
              const anyOption = getAnyResultOption(reelIndex);
              const match = selectedValue === ANY_RESULT_VALUE
                ? anyOption
                : options.find(option => option?.__resultOptionValue === selectedValue || option?.name === selectedValue);
              const imageField = normalizeImgField(match?.image);
              return {
                fieldKey,
                reelIndex,
                options,
                isOptIn: isOptInReel(slotSettings, reelIndex),
                hasSelection: !!match,
                isAnySelection: !!match?.__resultOptionIsAny,
                imageSrc: imageField.srcUrl || imgUrl(imageField.name, imageField.isDefault, step?.featureInfo?.defaultAssetFolder || 'randomiser', campaignID),
                label: match?.__resultOptionLabel || `Reel ${reelIndex + 1}`,
              };
            });

            return (
              <div
                key={rowIndex}
                style={{
                  background: 'var(--fc-circle-bg)',
                  border: `1px solid ${isRowActive ? C.sel : (row?.triggerDisruptor ? C.purple : C.border)}`,
                  borderRadius: '8px',
                  padding: '10px',
                  color: C.text,
                }}
                onClick={(event) => {
                  event.stopPropagation();
                  onGraphicClick?.(rowIndex);
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <strong style={{ fontSize: '11px', color: C.text }}>{`Result ${rowIndex + 1}`}</strong>
                    <span
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: comboChanceFraction ? '6px' : '0',
                        minWidth: comboChance == null ? 'auto' : '132px',
                        fontSize: '9px',
                        color: chanceBadgeStyles.color,
                        background: chanceBadgeStyles.background,
                        border: `1px solid ${chanceBadgeStyles.borderColor}`,
                        borderRadius: '999px',
                        padding: comboChance == null ? '2px 8px' : '5px 12px',
                        fontWeight: 700,
                        lineHeight: 1.1,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      <span>{formatChance(comboChance)}</span>
                      {comboChanceFraction ? (
                        <>
                          <span style={{ opacity: 0.45 }}>•</span>
                          <span style={{ fontSize: '8px', opacity: 0.92 }}>
                            {comboChanceFraction}
                          </span>
                        </>
                      ) : null}
                    </span>
                  </div>
                  {rows.length > 0 ? (
                    <button
                      type="button"
                      onClick={() => onSlotResultRemove?.(rowIndex)}
                      style={trashButtonStyle}
                    >
                      <span style={trashIconStyle}>
                        <span style={trashLidStyle} />
                        <span style={trashBarStyle} />
                        <span style={trashBodyStyle} />
                        <span style={{ ...trashLineBaseStyle, left: '4.4px' }} />
                        <span style={{ ...trashLineBaseStyle, left: '6.8px' }} />
                      </span>
                    </button>
                  ) : null}
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: `repeat(${reelCount}, minmax(0, 1fr))`, gap: '8px', marginBottom: '10px' }}>
                  {previews.map((preview, previewIndex) => {
                    return (
                      <div key={preview.fieldKey} style={{ position: 'relative' }}>
                        <div style={{ fontSize: '9px', color: C.textMuted, fontWeight: 700, marginBottom: '4px' }}>{`Reel ${previewIndex + 1}`}</div>
                        <button
                          type="button"
                          disabled={preview.isOptIn}
                          onClick={(event) => {
                            event.stopPropagation();
                            if (preview.isOptIn) return;
                            const triggerElement = event.currentTarget;
                            const pickerPosition = getPickerPosition(triggerElement.getBoundingClientRect());
                            setOpenPicker((current) => {
                              if (current?.rowIndex === rowIndex && current?.reelIndex === preview.reelIndex) {
                                return null;
                              }
                              return {
                                rowIndex,
                                reelIndex: preview.reelIndex,
                                triggerElement,
                                ...pickerPosition,
                              };
                            });
                          }}
                          style={{
                            width: '100%',
                            borderRadius: '8px',
                            overflow: 'hidden',
                            border: `1px solid ${C.border}`,
                            background: 'var(--fc-circle-bg)',
                            padding: '0',
                            cursor: preview.isOptIn ? 'not-allowed' : 'pointer',
                            opacity: preview.isOptIn ? 0.72 : 1,
                          }}
                        >
                          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'stretch' }}>
                            <div
                              style={{
                                height: '24px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                padding: '0 8px',
                                borderBottom: `1px solid ${C.border}`,
                              }}
                            >
                              <span
                                style={{
                                  fontSize: '10px',
                                  color: C.text,
                                  fontWeight: 700,
                                  whiteSpace: 'nowrap',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  width: '100%',
                                  textAlign: 'center',
                                }}
                              >
                                {preview.isOptIn ? 'Opt-In Reel' : (preview.hasSelection ? preview.label : 'Click to choose')}
                              </span>
                            </div>
                            <div style={{ height: '72px', background: 'var(--fc-circle-bg)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '4px' }}>
                              {preview.isOptIn ? (
                                <span style={{ fontSize: '10px', color: C.textMuted, fontWeight: 700 }}>OPT</span>
                              ) : preview.isAnySelection ? (
                                <span style={{ fontSize: '24px', color: C.textMuted, fontWeight: 700 }}>{ANY_RESULT_SYMBOL}</span>
                              ) : preview.imageSrc ? (
                                <img src={preview.imageSrc} alt={preview.label} style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                              ) : (
                                <span style={{ fontSize: '16px', color: C.textDim }}>+</span>
                              )}
                            </div>
                          </div>
                        </button>
                        {preview.isOptIn ? (
                          <div style={{ marginTop: '4px', fontSize: '8px', color: C.textMuted, textAlign: 'center' }}>
                            Disabled for winning combos
                          </div>
                        ) : null}
                      </div>
                    );
                  })}
                </div>

                <div
                  onDrop={(event) => onSlotResultDisruptorDrop?.(rowIndex, event)}
                  onDragOver={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                  }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: '8px',
                    borderRadius: '8px',
                    border: `1px solid ${row?.triggerDisruptor ? C.purple : C.border}`,
                    background: row?.triggerDisruptor ? 'rgba(145,70,255,0.12)' : 'rgba(255,255,255,0.03)',
                    padding: '7px 9px',
                    borderStyle: row?.triggerDisruptor ? 'solid' : 'dashed',
                  }}
                >
                  <span style={{ fontSize: '9.5px', color: row?.triggerDisruptor ? C.text : C.textDim, fontWeight: 600 }}>
                    {row?.triggerDisruptor ? `${triggerMeta.icon} ${triggerMeta.label}` : 'Drop end trigger component here'}
                  </span>
                  {row?.triggerDisruptor ? (
                    <button
                      type="button"
                      onClick={() => onSlotResultDisruptorRemove?.(rowIndex)}
                      style={trashButtonStyle}
                    >
                      <span style={trashIconStyle}>
                        <span style={trashLidStyle} />
                        <span style={trashBarStyle} />
                        <span style={trashBodyStyle} />
                        <span style={{ ...trashLineBaseStyle, left: '4.4px' }} />
                        <span style={{ ...trashLineBaseStyle, left: '6.8px' }} />
                      </span>
                    </button>
                  ) : null}
                </div>
              </div>
            );
          })}

          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              onSlotResultAdd?.();
              setOpenPicker(null);
            }}
            style={{
              border: `1px dashed ${C.border}`,
              background: 'var(--fc-circle-bg)',
              color: C.textDim,
              borderRadius: '8px',
              padding: '8px 10px',
              cursor: 'pointer',
              fontSize: '10px',
              fontWeight: 600,
            }}
          >
            Add winning result
          </button>
        </div>
      </foreignObject>
    </g>
    {openPicker && openPickerRow && openPickerFieldKey
      ? createPortal(
        <div
          ref={pickerMenuRef}
          style={{
            position: 'fixed',
            top: `${openPicker.top}px`,
            left: `${openPicker.left}px`,
            width: `${PICKER_MENU_W}px`,
            background: 'var(--fc-panel-bg)',
            border: `1px solid ${C.border}`,
            borderRadius: '10px',
            padding: `${PICKER_MENU_PAD}px`,
            boxShadow: '0 12px 30px rgba(0,0,0,0.35)',
            zIndex: 6000,
            maxHeight: `${PICKER_MENU_MAX_H}px`,
            overflowY: 'auto',
          }}
          onMouseDown={(event) => event.stopPropagation()}
          onClick={(event) => event.stopPropagation()}
        >
          {openPickerOptions.map((option) => {
            const optionImage = normalizeImgField(option?.image);
            const optionImageSrc = option?.__resultOptionIsAny
              ? ''
              : (optionImage.srcUrl || imgUrl(optionImage.name, optionImage.isDefault, step?.featureInfo?.defaultAssetFolder || 'randomiser', campaignID));
            return (
              <button
                key={option.__resultOptionKey}
                type="button"
                disabled={!option.__resultOptionSelectable}
                onMouseDown={(event) => {
                  event.stopPropagation();
                  event.preventDefault();
                  if (!option.__resultOptionSelectable) return;
                  handleResultOptionSelect(openPicker.rowIndex, openPickerFieldKey, option.__resultOptionValue);
                }}
                style={{
                  ...menuItemStyle,
                  opacity: option.__resultOptionSelectable ? 1 : 0.45,
                  cursor: option.__resultOptionSelectable ? 'pointer' : 'not-allowed',
                }}
              >
                <span style={{ width: '34px', height: '34px', borderRadius: '8px', overflow: 'hidden', background: 'var(--fc-circle-bg)', border: `1px solid ${C.border}`, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, padding: option.__resultOptionIsAny ? '0' : '2px' }}>
                  {option.__resultOptionIsAny ? (
                    <span style={{ fontSize: '18px', color: C.textMuted, fontWeight: 700 }}>{ANY_RESULT_SYMBOL}</span>
                  ) : optionImageSrc ? (
                    <img src={optionImageSrc} alt={option.__resultOptionLabel} style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                  ) : (
                    <span style={{ fontSize: '13px', color: C.textDim }}>+</span>
                  )}
                </span>
                <span style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                  <span style={{ fontSize: '10px', fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{option.__resultOptionLabel}</span>
                  {!option.__resultOptionSelectable ? (
                    <span style={{ fontSize: '8px', color: C.textDim }}>Name this image to use it here</span>
                  ) : null}
                </span>
              </button>
            );
          })}
        </div>,
        document.body,
      )
      : null}
    </>
  );
};

export default SlotResultsNode;